import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { ContentActionsService } from '~commonInfra/contentActions/ContentActions';
import { ChangeTagsActionHelperService } from '~content/services/ChangeTagsActionHelper';
import { CompositeSessionInfoSelectors, SessionUserSelectors } from '~selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~selectors/SiteUrlsSelectors';
import { incrementRouterSpaceReactKey } from '~src/app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { getAppStore } from '~src/app/AppStore';
import {
  ModifiedAtDetailSection,
  ModifiedByDetailSection
} from '~src/app/content/contentDetailDialog/CommonContentDetailSections';
import { sectionContainer } from '~src/app/content/contentDetailDialog/ContentDetailSection';
import { ConnectedDeleteContentSection } from '~src/app/content/contentDetailDialog/DeleteContentSection';
import {
  DESCRIPTION_MAX_LENGTH,
  DescriptionDetailSection
} from '~src/app/content/contentDetailDialog/DescriptionDetailSection';
import { ConnectedLocationDetailSection } from '~src/app/content/contentDetailDialog/LocationDetailSection';
import { ConnectedOwnerDetailSection } from '~src/app/content/contentDetailDialog/OwnerDetailSection';
import { TagsDetailSection } from '~src/app/content/contentDetailDialog/TagsDetailSection';
import { AppState } from '~src/app/state/AppState';
import { MapDispatchToProps, MapStateToProps } from '~src/app/utils/ConnectUtils';
import { Messages } from '~src/codegen/Localize';
import { AskDataIndexing, IDataSourceDetailInfoWithUsage, NlpSetting } from '~src/datasource/models/IDataSourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import { ISetDatasourceNlpSettingParams } from '~src/datasource/ServerApi/requests/SetDatasourceNlpSettingRequest';
import { IDatasourceActions } from '~src/datasource/ServerApi/types/IDatasourceActions';
import { ChangeDatasourceAskDataAction } from '~src/datasource/services/ChangeDatasourceAskDataAction';
import { ChangeDatasourceDescriptionAction } from '~src/datasource/services/ChangeDatasourceDescriptionAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { RevisionHistoryActionInstance } from '~src/revisionHistory/services/RevisionHistoryAction';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { Set } from '~src/ts/models/Set';

import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { ChangeDatasourceCertificationAction } from '../services/ChangeDatasourceCertificationAction';
import { ConnectedAskDataSettingsSection } from './AskData/AskDataSettingsSection';
import { DataCertificationSection } from './DataCertificationSection';
import { ConnectedLegacyAskDataSettingsSection } from './LegacyAskDataSettingsSection';
import { RemoteQueryAgentDetailSection } from './RemoteQueryAgentDetailSection';


interface DatasourceDetailsDialogBodyOwnProps {
  readonly datasource: IDataSourceDetailInfoWithUsage;
  readonly allowedActions: IDatasourceActions;
  readonly location: string;
}

interface DatasourceDetailsDialogBodyStateProps {
  readonly isAdminOrOwner: boolean;
  readonly urls: SpacesSiteUrls;
}

interface DatasourceDetailsDialogBodyDispatchProps {
  readonly incrementRouterSpaceReactKey: () => void;
}

export type DatasourceDetailsDialogBodyProps =
    DatasourceDetailsDialogBodyOwnProps &
    DatasourceDetailsDialogBodyStateProps &
    DatasourceDetailsDialogBodyDispatchProps;

export function DatasourceDetailsDialogBody(props: DatasourceDetailsDialogBodyProps): JSX.Element {
  const { datasource, allowedActions, isAdminOrOwner, urls, location, incrementRouterSpaceReactKey } = props;

  function allowedAction(actionName: keyof IDatasourceActions): boolean {
    return !!(allowedActions?.[actionName]);
  }

  const setDescription = React.useCallback((description: string) => {
    return ChangeDatasourceDescriptionAction.execute(datasource, description);
  }, [datasource]);

  const moveToProject = React.useCallback(() => {
    return ContentActionsService.moveDatasourcesToProject(Set.singletonForIdItem(datasource));
  }, [datasource]);

  const setTags = React.useCallback((tags: string[]) => {
    return ChangeTagsActionHelperService.changeTagsForContent([datasource], datasource.tags, tags)
        .then((results: IResult[]) => ResultHelper.combineResults(results));
  }, [datasource]);

  const showRevisionHistory = React.useCallback(() => {
    return RevisionHistoryActionInstance.showDataSourceRevisionHistory(datasource);
  }, [datasource]);

  const setNlpSettings = React.useCallback((nlpOption: NlpSetting) => {
    const params: ISetDatasourceNlpSettingParams = {
      id: datasource.id,
      nlpOption,
    };

    return DataSources.setNlpSetting(params);
  }, [datasource]);

  const setAskDataSettings = React.useCallback((indexing: AskDataIndexing) => {
    return ChangeDatasourceAskDataAction.execute(datasource, indexing);
  }, [datasource]);

  const setCertification = React.useCallback((isCertified: boolean, certificationNote: string) => {
    return ChangeDatasourceCertificationAction.execute(datasource, { isCertified, certificationNote });
  }, [datasource]);

  const allowRemoteQueryAgentDetailSection =
    FeatureFlags.bridgeLiveDirectAssignEnabled &&
    !props.datasource.hasExtracts &&
    !props.datasource.testingConnectionAllowed;

  const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
  const isOwner = datasource.owner? (userId === datasource.owner.id) : true;
  return (
    <div className={sectionContainer}>
      <DescriptionDetailSection
        description={datasource.description}
        isActionEnabled={allowedAction('setDescription')}
        setDescription={setDescription}
        descriptionMaxLength={DESCRIPTION_MAX_LENGTH}
        showPreviewAndFormattingHints={true}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      {
        <ConnectedLocationDetailSection
        location={location}
        project={datasource.project}
        isActionEnabled={allowedAction('setProject')}
        content={datasource}
        urls={urls}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
        isOwner={isOwner}
      />
      }
      <ConnectedOwnerDetailSection content={datasource} isActionEnabled={allowedAction('setOwner')} urls={urls}/>
      <TagsDetailSection
        tags={datasource.tags}
        isActionEnabled={allowedAction('setTags')}
        setTags={setTags}
        tagUrlFn={(tag: string) => (urls.datasourcesWithTagUrl(tag))}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      <ModifiedAtDetailSection
        date={datasource.updatedAt}
        allowShowRevisionHistory={allowedAction('versionManagement')}
        showRevisionHistory={showRevisionHistory}
      />
      {/*DEBUG SECTION ONLY*/
        allowRemoteQueryAgentDetailSection &&
          <RemoteQueryAgentDetailSection datasource={datasource} />
      }
      {!FeatureFlags.nlplensContentTypeEnabled &&
        <ConnectedLegacyAskDataSettingsSection
          isAdminOrOwner={isAdminOrOwner}
          setNlpSettings={setNlpSettings}
          incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
        />
      }
      {FeatureFlags.nlplensContentTypeEnabled &&
        <ConnectedAskDataSettingsSection
          isAdminOrOwner={isAdminOrOwner}
          setAskDataSettings={setAskDataSettings}
          incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
        />
      }
      {FeatureFlags.datasourceCertificationEnabled &&
        <DataCertificationSection
          isCertified={datasource.certificationDetails.isCertified}
          certifier={datasource.certificationDetails.certifier}
          note={datasource.certificationDetails.certificationNote}
          isActionEnabled={allowedAction('setCertification')}
          urls={urls}
          setCertification={setCertification}
          incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
        />
      }
      {FeatureFlags.modifiedByUserEnabled &&
        <ModifiedByDetailSection user={datasource.modifiedBy} urls={urls} />
      }
      {allowedAction('del') &&
        <ConnectedDeleteContentSection
          actionMessage={Messages.deleteDataSource_action()}
          content={datasource}
        />
      }
    </div>
  );
}

const mapDispatchToProps: MapDispatchToProps<DatasourceDetailsDialogBodyDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey: incrementRouterSpaceReactKey
}, dispatch);

const mapStateToProps: MapStateToProps<DatasourceDetailsDialogBodyStateProps> = (state: AppState, ownProps: DatasourceDetailsDialogBodyOwnProps) => ({
  isAdminOrOwner: CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(ownProps.datasource.owner)(state),
  urls: siteUrlsSelector(state),
});

export const ConnectedDatasourceDetailsDialogBody: React.ComponentClass<DatasourceDetailsDialogBodyOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(DatasourceDetailsDialogBody);


